import { AttributeDefinitionType } from "./types";
import { captureMessage } from "@sentry/react";

interface Attribute {
  id: number;
  value: boolean | string | number;
  type: AttributeDefinitionType;
}

// --- US Ethnicity Specific Constants ---
const US_ETHNICITY_ATTRIBUTES = {
  ethnicity1: 22520, // Radio 1
  ethnicity2: 22521, // Radio 2
  hispanicLatino: 22519, // Boolean
  multiRacial: 22500, // Boolean for multiple ethnicities selected
} as const;

type UsEthnicityOption =
  | "American Indian or Alaska Native"
  | "Asian"
  | "Black or African American"
  | "Native Hawaiian or Other Pacific Islander"
  | "White"
  | "Other"
  | "I'd prefer not to answer"
  | "Hispanic or Latino";

const US_ETHNICITY_RADIO_VALUES: Record<
  Exclude<UsEthnicityOption, "Hispanic or Latino">,
  { first: string; second: string }
> = {
  "American Indian or Alaska Native": {
    first: "000-6ls1x",
    second: "000-9c7gt",
  },
  Asian: { first: "001-xcv8ve", second: "001-cnn2pt" },
  "Black or African American": { first: "002-uumkxi", second: "002-fwz8kf" },
  "Native Hawaiian or Other Pacific Islander": {
    first: "003-lnrhpu",
    second: "003-rk0r6j",
  },
  White: { first: "004-1addnv", second: "004-3ef4lq" },
  Other: { first: "005-1o2epp", second: "005-gbj0dm" },
  "I'd prefer not to answer": { first: "006-6i6gur", second: "006-15be6" },
} as const;
// --- End US Ethnicity Specific Constants ---

// --- Canadian Ethnicity Specific Constants ---
export const CA_ATTRIBUTE_IDS = {
  indigenousIdentity: 26317, // First nations attribute ID ~ Radio Attribute Type
  ethnicity1: 26313, // Attribute ID for  Ethncity question 1
  ethnicity2: 26314, // Attribute ID Ethnicity question 2
  multiRacial: 22500, // Boolean for multiple ethnicities selected
} as const;

export const CA_INDIGENOUS_IDENTITY_OPTIONS = {
  no: {
    id: "000-n1fxyb",
    label: "No, not First Nations, Métis or Inuk (Inuit)",
  },
  firstNations: {
    id: "001-b2flxl",
    label: "Yes, First Nations (North American Indian)",
  },
  metis: { id: "002-d2x7ku", label: "Yes, Métis" },
  inuk: { id: "003-pio8w4", label: "Yes, Inuk (Inuit)" },
} as const;
export type CaIndigenousOptionId =
  (typeof CA_INDIGENOUS_IDENTITY_OPTIONS)[keyof typeof CA_INDIGENOUS_IDENTITY_OPTIONS]["id"];

export const CA_ETHNICITY_OPTIONS_MAP = {
  primary: {
    White: "001-9a30j",
    "South Asian (e.g., East Indian, Pakistani, Sri Lankan)": "001-drrtn",
    Chinese: "002-kk7n5v",
    Black: "003-gvwgt",
    Filipino: "004-xa7rq",
    Arab: "005-mybp1s",
    "Latin American": "006-i1pbsb",
    "Southeast Asian (e.g., Vietnamese, Cambodian, Laotian, Thai)":
      "007-cnibbv",
    "West Asian (e.g., Iranian, Afghan)": "008-k3wrrp",
    Korean: "009-ee1tf",
    Japanese: "010-a85ghf",
    Other: "011-ilo4nh",
    "I'd prefer not to answer": "012-0hgvzo",
  },
  secondary: {
    White: "000-n02oc6",
    "South Asian (e.g., East Indian, Pakistani, Sri Lankan)": "001-8unt2",
    Chinese: "002-8hjk9y",
    Black: "003-10q2o",
    Filipino: "004-rer778",
    Arab: "005-lmytkb",
    "Latin American": "006-92ezdk",
    "Southeast Asian (e.g., Vietnamese, Cambodian, Laotian, Thai)":
      "007-dtygqb",
    "West Asian (e.g., Iranian, Afghan)": "008-oxqvff",
    Korean: "009-c5m14",
    Japanese: "010-3c7znu",
    Other: "011-2p1jo8",
    "I'd prefer not to answer": "012-r039zx",
  },
} as const;

export type CaEthnicityKey = keyof typeof CA_ETHNICITY_OPTIONS_MAP.primary;
export type CaEthnicityOption = { id: string; label: CaEthnicityKey };

export const CA_ETHNICITY_PRIMARY_OPTIONS: CaEthnicityOption[] = Object.entries(
  CA_ETHNICITY_OPTIONS_MAP.primary,
).map(([label, id]) => ({ id, label: label as CaEthnicityKey }));

export const CA_ETHNICITY_SECONDARY_OPTIONS: CaEthnicityOption[] =
  Object.entries(CA_ETHNICITY_OPTIONS_MAP.secondary).map(([label, id]) => ({
    id,
    label: label as CaEthnicityKey,
  }));

// Structure expected by the form schema
export interface CanadianEthnicitySchema {
  caIndigenousIdentity?: CaIndigenousOptionId;
  caEthnicity1?: string[];
  caEthnicity2?: string[];
}

export const PREFER_NOT_TO_ANSWER_ID_CA_1 =
  CA_ETHNICITY_OPTIONS_MAP.primary["I'd prefer not to answer"];

export const PREFER_NOT_TO_ANSWER_ID_CA_2 =
  CA_ETHNICITY_OPTIONS_MAP.secondary["I'd prefer not to answer"];

// --- End Canadian Ethnicity Specific Constants ---

/**
 * Maps the contact form ethnicity checkboxes to the target system's attributes
 * This function is specific to the US ethnicity mapping.
 */
export function mapUsEthnicityToAttributes(ethnicities: string[]): Attribute[] {
  const attributes: Attribute[] = [];

  // Always map Hispanic/Latino boolean
  attributes.push({
    id: US_ETHNICITY_ATTRIBUTES.hispanicLatino,
    value: ethnicities.includes("Hispanic or Latino"),
    type: AttributeDefinitionType.BOOLEAN,
  });

  // Get non-Hispanic ethnicities and sort them alphabetically
  const nonHispanicEthnicities = ethnicities
    .filter(
      (e): e is Exclude<UsEthnicityOption, "Hispanic or Latino"> =>
        e !== "Hispanic or Latino" && e in US_ETHNICITY_RADIO_VALUES,
    )
    .sort((a, b) => a.localeCompare(b));

  // Map first ethnicity to ethnicity1 if it exists
  if (nonHispanicEthnicities.length > 0) {
    attributes.push({
      id: US_ETHNICITY_ATTRIBUTES.ethnicity1,
      value: US_ETHNICITY_RADIO_VALUES[nonHispanicEthnicities[0]].first,
      type: AttributeDefinitionType.RADIO,
    });

    // Map second ethnicity to ethnicity2 if it exists
    if (nonHispanicEthnicities.length > 1) {
      attributes.push({
        id: US_ETHNICITY_ATTRIBUTES.ethnicity2,
        value: US_ETHNICITY_RADIO_VALUES[nonHispanicEthnicities[1]].second,
        type: AttributeDefinitionType.RADIO,
      });
    }
  }

  // A user is considered multi-racial if they select:
  // 1. Two or more non-Hispanic/Latino ethnicities.
  // 2. Exactly one non-Hispanic/Latino ethnicity AND "Hispanic or Latino".
  const isMultiRacial = nonHispanicEthnicities.length >= 2;

  attributes.push({
    id: US_ETHNICITY_ATTRIBUTES.multiRacial,
    value: isMultiRacial,
    type: AttributeDefinitionType.BOOLEAN,
  });

  return attributes;
}

/**
 * Maps ethnicity selections to Canadian ethnicity attributes for the backend system
 *
 * IMPORTANT REQUIREMENTS:
 * 1. We should only send a maximum of 2 ethnicity-related attributes total
 * 2. Indigenous identity is optional but required if any other ethnicity is selected
 * 3. For indigenous users (any "Yes" option):
 *    - Indigenous identity is mapped to ethnicity1
 *    - Indigenous identity is also mapped to indigenousIdentity attribute (26317)
 *    - NO additional ethnicity selections allowed (only indigenous identity is used)
 * 4. For non-indigenous users ("No" option):
 *    - We DO NOT send the "No" selection to the backend
 *    - We only send their actual ethnicity selection(s)
 *
 * @param ethnicities Array of ethnicity labels selected by the user
 * @returns Array of Attributes formatted for the backend system
 */
export function mapCanadianEthnicityToAttributes(
  ethnicities: string[],
): Attribute[] {
  const attributes: Attribute[] = [];

  // If no ethnicities selected, return empty array
  if (ethnicities.length === 0) {
    return attributes;
  }

  // STEP 1: Find indigenous identity selection
  const indigenousIdentity = Object.values(CA_INDIGENOUS_IDENTITY_OPTIONS).find(
    (option) => ethnicities.includes(option.label),
  );

  // If no indigenous identity but other ethnicities, we can't proceed
  // Note: This validation logic should happen in the form, but we handle it here too
  if (!indigenousIdentity) {
    captureMessage(
      "No indigenous identity selected but other ethnicities present. Configuration error for Canada it is assumed that there is a recorded indigenous identity.",
      {
        level: "error",
        extra: {
          ethnicities,
          indigenousIdentity,
        },
      },
    );
    return attributes;
  }

  // STEP 2: Extract ethnicity selections (excluding indigenous options)
  const ethnicitySelections = ethnicities
    .filter(
      (e) =>
        !Object.values(CA_INDIGENOUS_IDENTITY_OPTIONS).some(
          (option) => option.label === e,
        ),
    )
    .sort((a, b) => a.localeCompare(b));

  // STEP 3: Determine if user is non-indigenous (selected "No" option)
  const isNonIndigenous =
    indigenousIdentity.label === CA_INDIGENOUS_IDENTITY_OPTIONS.no.label;

  // STEP 4: Map ethnicity selections according to path

  // For indigenous users (any "Yes" option)
  if (!isNonIndigenous) {
    // Map indigenous identity to ethnicity1
    attributes.push({
      id: CA_ATTRIBUTE_IDS.ethnicity1,
      value: indigenousIdentity.id,
      type: AttributeDefinitionType.RADIO,
    });

    // Map indigenous identity to the indigenousIdentity attribute (26317)
    attributes.push({
      id: CA_ATTRIBUTE_IDS.indigenousIdentity,
      value: indigenousIdentity.id,
      type: AttributeDefinitionType.RADIO,
    });

    // No additional ethnicity mapping for indigenous users
  }

  // For non-indigenous users ("No" option), we don't send the "No" selection
  else {
    // Map only their actual ethnicity selection(s)
    if (ethnicitySelections.length > 0) {
      const firstEthnicity = ethnicitySelections[0];
      if (firstEthnicity in CA_ETHNICITY_OPTIONS_MAP.primary) {
        const ethnicity1Value =
          CA_ETHNICITY_OPTIONS_MAP.primary[firstEthnicity as CaEthnicityKey];
        attributes.push({
          id: CA_ATTRIBUTE_IDS.ethnicity1,
          value: ethnicity1Value,
          type: AttributeDefinitionType.RADIO,
        });
      }

      // If they selected a second ethnicity, map it to ethnicity2
      if (ethnicitySelections.length > 1) {
        const secondEthnicity = ethnicitySelections[1];
        if (secondEthnicity in CA_ETHNICITY_OPTIONS_MAP.secondary) {
          const ethnicity2Value =
            CA_ETHNICITY_OPTIONS_MAP.secondary[
              secondEthnicity as CaEthnicityKey
            ];
          attributes.push({
            id: CA_ATTRIBUTE_IDS.ethnicity2,
            value: ethnicity2Value,
            type: AttributeDefinitionType.RADIO,
          });
        }
      }

      // Explicitly set indigenousIdentity to "no" for non-indigenous users
      attributes.push({
        id: CA_ATTRIBUTE_IDS.indigenousIdentity,
        value: "000-n1fxyb", // The id of the "No" option
        type: AttributeDefinitionType.RADIO,
      });
    }
  }

  // Add multi-racial attribute
  // For Canadian implementation, only count non-indigenous ethnicities
  const isMultiRacial = ethnicitySelections.length >= 2;
  attributes.push({
    id: CA_ATTRIBUTE_IDS.multiRacial,
    value: isMultiRacial,
    type: AttributeDefinitionType.BOOLEAN,
  });

  return attributes;
}
